import * as React from 'react';

import { safeCall } from '@xemplo/safe-call';

import Toast from '../toast';

import { ToastPopoverContainer, ToastWrapper } from './toast-provider.style';
import {
  AddToastProps,
  ToastHorizontalPosition,
  ToastVerticalPosition,
} from './toast-provider.types';

const DEFAULT_TOAST_DURATION = 2500;

export const useToast = () => {
  return React.useContext(ToastContext);
};

export const ToastContext = React.createContext({
  addToast: (props: AddToastProps): void => {
    throw new Error('Render ToastProvider to add toasts.');
  },
});

export function ToastProvider({ children }: Readonly<React.PropsWithChildren>) {
  const [toasts, setToasts] = React.useState([] as Array<AddToastProps>);

  const addToast = React.useCallback((toast: AddToastProps) => {
    const newToast = {
      ...toast,
      position: {
        vertical: toast?.position?.vertical ?? ToastVerticalPosition.Bottom,
        horizontal: toast?.position?.horizontal ?? ToastHorizontalPosition.Right,
      },
    };
    setToasts((toasts) => [...toasts, newToast]);
    setTimeout(
      () => setToasts((toasts) => toasts.slice(1)),
      toast.duration ?? DEFAULT_TOAST_DURATION,
    );
  }, []);

  const contextValue = React.useMemo(
    () => ({
      addToast,
    }),
    [addToast],
  );

  const verticalPositions = Object.values(ToastVerticalPosition);
  const horizontalPositions = Object.values(ToastHorizontalPosition);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const hasToasts = toasts.length > 0;

  React.useEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    el.setAttribute('popover', 'manual');
  }, []);

  React.useEffect(() => {
    const el = popoverRef.current;
    if (!el) return;

    if (hasToasts) {
      // hidePopover() throws an `InvalidStateError` if the popover is not
      // currently showing. We intentionally swallow that error because the
      // hide-then-show sequence is needed to re-promote the popover to the
      // top of the browser's top-layer stack (above any newly opened dialogs).
      safeCall(() => el.hidePopover());
      el.showPopover();
    } else {
      safeCall(() => el.hidePopover());
    }
  }, [hasToasts]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastPopoverContainer ref={popoverRef}>
        {verticalPositions.map((vPos) =>
          horizontalPositions.map((hPos) => (
            <ToastWrapper
              key={`toast-${hPos}_${vPos}`}
              $position={{ vertical: vPos, horizontal: hPos }}
            >
              {toasts
                .filter(
                  (props) =>
                    props?.position?.vertical === vPos &&
                    props.position.horizontal === hPos,
                )
                .map((props) => (
                  <Toast key={props.text} {...props} />
                ))}
            </ToastWrapper>
          )),
        )}
      </ToastPopoverContainer>
    </ToastContext.Provider>
  );
}
